import { useState } from 'react';
import { get } from 'lodash';
import { useSnackbar } from 'notistack';
import { BulkAddCredit, Credit } from 'lib/types/credits';
import { useApi } from 'lib/contexts/ApplicationState';
import { Role } from 'lib/types';
import { User } from 'lib/types/users';
import { EditableCreditItem } from 'components/CreditsTableView/EditableCreditItem';
import { AddCredits, useAddCredits } from 'components/AddCredits';
import { ContentCopy } from '@mui/icons-material';
import { Method } from './AddDrawer';

interface Props {
  postIds: number[];
  credits: Credit[];
  method?: Method;
  onChange: (message?: string) => void;
  onCancel: () => void;
}

export const CreditDrawer = ({
  postIds,
  credits,
  onChange,
  method,
  onCancel,
}: Props) => {
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const { addCredits } = useAddCredits();

  const [isSaving, setIsSaving] = useState(false);

  const copy = async () => {
    setIsSaving(true);
    try {
      const payload: BulkAddCredit = {
        postIds,
        credits: credits.map((c) => ({
          roleId: c.roleId,
          userId: c.userId,
          isPrivate: c.isPrivate,
        })),
      };
      await API.createCredits(payload);
      onChange('Credit(s) have been copied.');
    } catch (error) {
      const msg = get(error, 'message');
      enqueueSnackbar(`There was an error copying the credit(s): ${msg}`, { variant: 'error' });
    }
    setIsSaving(false);
  };

  const save = async (
    user: User, roles: Role[], isPrivate?: boolean,
  ) => {
    try {
      await addCredits(postIds, user, roles, isPrivate);
      if (onChange) onChange('Credit(s) have been added.');
    } catch (e) {
      const msg = get(e, 'message');
      enqueueSnackbar(`There was an error adding the credit(s): ${msg}`, { variant: 'error' });
    }
  };

  if (isSaving) return <div className="w-full text-center mt-6">Saving...</div>;

  if (postIds.length === 0) return <div className="pt-4 font-semibold">Please select at least one post to credit</div>;

  if (!method) return null;

  if (method === 'copy') {
    return (
      <div className="my-4">
        {credits.length === 0 ? (
          <div className="mb-4">
            Please click the copy icon
            <span className="mx-2"><ContentCopy /></span>
            on a post to select credits to copy
          </div>
        )
          : (
            <>
              <div className="font-bold text-xl mb-2">
                Copy these credits to selected posts
              </div>
              <ul>
                {credits.map((credit) => (
                  <li key={credit.id}>
                    <EditableCreditItem
                      credit={credit}
                      onStartEdit={() => { }}
                      hideEdit
                    />
                  </li>
                ))}
              </ul>

            </>
          )}
        <div className="text-right">
          <button
            type="button"
            className="btn-primary-outlined mr-2"
            aria-label="cancel copy credit"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            aria-label="copy credit"
            onClick={copy}
            disabled={postIds.length <= 0 || credits.length <= 0}
          >
            {postIds.length <= 0 ? 'Select posts to save' : 'Save'}
          </button>
        </div>

      </div>
    );
  }

  return (
    <AddCredits
      postId={postIds[0]}
      bulk
      credits={[]}
      onSave={save}
      onCancel={onCancel}
    />
  );
};
